import { createRouter, createWebHistory } from 'vue-router'
import { useStoreOrder } from '@/stores/order';
import { useStoreRestaurant } from '@/stores/restaurant';
import { useStoreCustomer } from '@/stores/customer';
import { useStoreAnalytics } from '@/stores/analytics';
import { useStoreApp } from '@/stores/app';
import { useStoreCheckout } from '@/stores/checkout';

import { logger } from '../plugins/logger';
import { STATUS } from '../composables/order';

let routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/Home.vue'),
    meta: { transition: 'slide-right' },
  },
  {
    path: '/order-type',
    name: 'order-type',
    component: () => import('@/views/OrderType.vue'),
    meta: { transition: 'slide-right' },
  },
  {
    path: '/order/:action?',
    name: 'order',
    meta: {
      resource: 'order',
    },
    component: () => import('@/views/Order.vue'),
    meta: { transition: 'slide-right' },
  },
  {
    path: '/restaurants',
    meta: {
      resource: 'checkout',
      transition: 'slide-right'
    },
    children: [
      {
        path: '',
        name: 'restaurants',
        component: () => import('@/views/restaurants/Restaurants.vue'),
      },
    ]
  },
  {
    path: '/checkout',
    meta: {
      resource: 'checkout',
      transition: 'slide-right',
      auth: canOrder(),
    },
    children: [
      {
        path: 'sent',
        name: 'checkout-sent',
        component: () => import('@/views/checkout/Sent.vue'),
      },
      {
        path: '',
        name: 'checkout',
        component: () => import('@/views/checkout/Checkout.vue'),
      },
      {
        path: 'payment',
        name: 'checkout-payment',
        component: () => import('@/views/checkout/Payment.vue'),
      }
    ]
  },
  {
    path: '/profile',
    meta: {
      resource: 'profile',
      transition: 'slide-right',
    },
    children: [
      {
        path: 'info',
        name: 'profile-info',
        component: () => import('@/views/profile/ProfileInfo.vue'),
      },
      {
        path: 'rewards',
        name: 'rewards',
        component: () => import('@/views/profile/ProfileRewards.vue'),
      },
      {
        path: 'addresses',
        name: 'addresses',
        component: () => import('@/views/profile/Addresses.vue'),
      },
      {
        path: 'order-history',
        name: 'order-history',
        component: () => import('@/views/profile/ProfileOrderHistory.vue'),
      },
      {
        path: 'scan-qr-code/:context?',
        name: 'scan-qr-code',
        component: () => import('@/views/profile/ScanQrCode.vue'),
      },
      {
        path: 'login',
        name: 'login',
        component: () => import('@/views/profile/Login.vue'),
      },
      {
        path: 'signup',
        name: 'signup',
        component: () => import('@/views/profile/Signup.vue'),
      },
      {
        path: 'loyalty-signup',
        name: 'loyalty-signup',
        component: () => import('@/views/profile/LoyaltySignup.vue'),
      },
      {
        path: '',
        name: 'profile',
        component: () => import('@/views/profile/Profile.vue'),
      },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: () => import('@/views/profile/ForgotPassword.vue'),
      }
    ],
  },
  {
    path: '/tracking',
    name: 'checkout-tracking',
    meta: {
      resource: 'checkout',
      transition: 'slide-right',
    },
    component: () => import('@/views/Tracking.vue'),
  },
  {
    name: 'tablebooking',
    path: '/tablebooking',
    component: () => import('@/views/Tablebooking.vue'),
  },
  {
    path: '/not-found',
    name: 'not-found',
    component: () => import('@/views/errors/404.vue'),
  },
  {
    path: '/:pathMatch(.*)',
    redirect: '/',
  }
];

if (process.env.NODE_ENV === 'development') {
  routes.push({ name: 'test', path: '/test', component: () => import('@/views/Test.vue') });
}

const LOGGER_TAG = 'router';
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { el: to.hash };
    }
    return { x: 0, y: 0 };
  }
});

function canOrder() {
  return () => {
    let oStore = useStoreOrder();

    if (!oStore.hasDeliveryMethods) {
      return false;
    }

    return oStore.canOrder.data;
  }
}

router.beforeEach(async (to, from) => {
  let oStore = useStoreOrder();
  let rStore = useStoreRestaurant();
  let cStore = useStoreCustomer();
  let checkoutStore = useStoreCheckout();
  let appStore = useStoreApp();

  logger.info(LOGGER_TAG, from.name, to.name, to.query);

  // aspetta che rStore.isLoading sia true, isLoading non è async è una ref di vue quindi non puoi usare await
  while (rStore.isLoading) {
    logger.info(LOGGER_TAG, 'waiting for rStore.isLoading to be false');
    await new Promise(resolve => setTimeout(resolve, 100));
  }


  let { sub, to: $to, tbl, subrestaurant_id, dm, dmethod } = to.query;

  // ordine dal tavolo tramite qs
  if ($to && $to == 'table') {
    try {
      let pos = null;
      if (sub) {
        pos = Object.values(rStore.poss).find(p => p.uid == sub);
        if (!pos) {
          return;
        }
        await oStore.selectPos(pos);
      }

      await oStore.setDeliveryMethod({ dmethod: '0t', otable: true, tbl, delivery: 0, subrestaurant_id: pos ? pos.id : null });
      await oStore.getOrder();
      return '/order';
    } catch (error) {
      return '/';
    }
  }

  // ordine generico tramite qs
  if ((subrestaurant_id || sub) || (dm || dmethod)) {
    sub = sub ?? subrestaurant_id;
    dmethod = dmethod ?? dm;
    let dmPayload = { dmethod };

    logger.info(LOGGER_TAG, 'subrestaurant_id', sub, 'dmethod', dmethod);

    let pos = null;
    if (sub) {
      pos = Object.values(rStore.poss).find(p => p.uid == sub);
      if (pos) {
        logger.info(LOGGER_TAG, 'selecting pos', pos);
        await oStore.selectPos(pos);
        dmPayload.subrestaurant_id = pos.id;
      }
    } else if (!sub && rStore.restaurant.has_subrestaurants) {
      return '/';
    }

    logger.info(LOGGER_TAG, 'setting delivery method', dmPayload);

    await oStore.setDeliveryMethod(dmPayload);
    await oStore.getOrder();
    return '/order';
  }


  if (to.meta.auth) {
    let authRun = to.meta.auth();
    logger.info(LOGGER_TAG, 'authRun', authRun);
    if (!authRun) {
      return '/';
    }
  }

  // if i'm going from tracking to checkout make a redirect to order page
  if (from.name === 'checkout-tracking' && (to.name === 'checkout' || to.name === 'checkout-sent')) {
    return '/';
  }

  // if i'm going from tracking to checkout make a redirect to order page
  if (from.name === 'checkout-sent' && (to.name === 'checkout' || to.name === 'checkout-payment')) {
    return '/';
  }

  if (to.name === 'order' || to.name === 'checkout') {
    let order = oStore.order;
    if (order) {
      let status = order.status.value;
      // ho completato un ordine ma se devo tornare all'ordine o al carrello devo scaricare un nuovo oggetto ordine.
      if (status !== STATUS.TEMP) {
        logger.info(LOGGER_TAG, 'reload order', status);
        await oStore.getOrder();
      }
    }

    logger.info(LOGGER_TAG, 'order', to.name, from.name, oStore.deliveryMethod, oStore.isDeliveryWithAddress, oStore.addressIsValid);
    if ((to.name === 'order' && from.name !== 'order') && oStore.deliveryMethod && (oStore.isDeliveryWithAddress && !oStore.addressIsValid)) {
      let response = await oStore.doAct('address_validation', null, {
        address: oStore.order.address,
        city: oStore.order.city,
        zip: oStore.order.zip,
        country: oStore.order.country,
        place_id: oStore.order.place_id,
        lat: oStore.order.lat,
        lng: oStore.order.lng
      });
      if (!response.valid) {
        return '/order-type'
      }
      return '/order';
    }

    logger.info(LOGGER_TAG, 'oStore delivery method', oStore.deliveryMethod);
    if (!oStore.deliveryMethod) {
      return '/';
    }
  }

  // If we're going to any route under /profile and the user is not logged in,
  if (to && to.path && to.path.startsWith('/profile') && !cStore.customer) {
    const allowedUnauthTargets = ['login', 'signup', 'loyalty-signup', 'forgot-password', 'rewards'];
    if (allowedUnauthTargets.includes(to.name)) {
      // allow the navigation to proceed
      return;
    }

    if (from.name === 'login') {
      return '/home';
    }

    return {
      name: 'login',
      query: {
        redirect: to.query.redirect
      }
    };
  }

  // if i'm already logged in block login and signup route
  if (['login', 'signup'].includes(to.name) && cStore.customer) {
    if (from.name === 'profile') {
      return '/order';
    }
    return '/profile';
  }

  // da checkout/payment a checkout togli il lock dall'ordine
  if (from.name === 'checkout-payment' && to.name === 'checkout') {
    let order = await checkoutStore.resumeOrder();
    if (!order) {
      logger.info(LOGGER_TAG, 'resumeOrder not done, redirecting to home');
      return '/';
    }
  }
});


router.afterEach((to, from) => {
  const analyticsStore = useStoreAnalytics();
  if (to.name !== 'not-found') {
    logger.info(LOGGER_TAG, 'Navigated to:', to.fullPath);
    analyticsStore.triggerEvent('PageView', { path: to.fullPath })
  }
})

export default router;
